import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Gift, Package, RefreshCw, Zap } from 'lucide-react';
import {
  getQuotaColor,
  resetQuotaForTesting,
  hasUserEverPurchased,
} from '../utils/subscriptionUtils';
import EarnAnalysesModal from './EarnAnalysesModal';
import type { QuotaInfo, UserSubscriptionDB } from '../types';

interface QuotaIndicatorProps {
  quota: QuotaInfo;
  currentPlan: UserSubscriptionDB;
  onUpgradeClick: () => void;
}

const QuotaIndicator: React.FC<QuotaIndicatorProps> = ({
  quota,
  currentPlan,
  onUpgradeClick,
}) => {
  const [showEarnModal, setShowEarnModal] = useState(false);
  const { t } = useTranslation();
  const progressColor = getQuotaColor(quota.percentage);
  const remaining = quota.limit - quota.current;

  const handleResetQuota = () => {
    if (confirm(t('quota_indicator.reset_confirm'))) {
      resetQuotaForTesting();
      window.location.reload();
    }
  };

  useEffect(() => {}, [currentPlan, quota]);

  const handleEarnSuccess = () => {
    // Reload page to update quota
    window.location.reload();
  };

  // Check if user has ever made a purchase
  const hasEverPurchased = hasUserEverPurchased();
  const showWelcomeOffer = currentPlan.plan === 'free' && !hasEverPurchased;

  // NEW LOGIC: Don't show QuotaIndicator if user has already purchased
  if (hasEverPurchased) {
    return null;
  }

  return (
    <>
      <div
        className={`rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6 border transition-all duration-500 relative overflow-hidden ${
          currentPlan.plan === 'free'
            ? 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-indigo-200 hover:shadow-lg transform hover:scale-[1.02]'
            : 'bg-white border-gray-200'
        }`}
      >
        {/* Background animation for free plan */}
        {currentPlan.plan === 'free' && (
          <div className="absolute inset-0 rounded-xl opacity-20 pointer-events-none">
            <div className="absolute top-2 sm:top-4 left-2 sm:left-4 w-2 sm:w-3 h-2 sm:h-3 bg-blue-400 rounded-full animate-ping"></div>
            <div className="absolute top-4 sm:top-6 right-4 sm:right-8 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-indigo-400 rounded-full animate-bounce animation-delay-500"></div>
            <div className="absolute bottom-4 sm:bottom-6 left-6 sm:left-12 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-purple-400 rounded-full animate-pulse animation-delay-1000"></div>
            <div className="absolute bottom-2 sm:bottom-4 right-4 sm:right-6 w-2 sm:w-3 h-2 sm:h-3 bg-pink-400 rounded-full animate-ping animation-delay-2000"></div>
          </div>
        )}

        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3 sm:gap-0">
            <div>
              <h3
                className={`text-base sm:text-lg font-semibold flex items-center ${
                  currentPlan.plan === 'free'
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'
                    : 'text-gray-900'
                }`}
              >
                {currentPlan.plan === 'free' ? (
                  <Gift className="w-5 h-5 mr-2 text-blue-500 animate-bounce" />
                ) : (
                  <Package className="w-5 h-5 mr-2 text-blue-500" />
                )}
                {t('profile.subscription.analyses')}
              </h3>
              <p
                className={`text-xs sm:text-sm ${
                  currentPlan.plan === 'free'
                    ? 'text-indigo-700 font-medium'
                    : 'text-gray-600'
                }`}
              >
                {t('quota_indicator.remaining_analyses', {
                  count: remaining,
                })}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              {/* Test button to reset quota */}
              {/* <button
                onClick={handleResetQuota}
                className="flex items-center justify-center px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors text-xs sm:text-sm w-full sm:w-auto"
                title={t('quota_indicator.reset_test')}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                {t('quota_indicator.reset_test')}
              </button> */}

              <button
                onClick={onUpgradeClick}
                className={`flex items-center justify-center px-4 py-2 rounded-lg transition-all transform hover:-translate-y-1 text-xs sm:text-sm w-full sm:w-auto ${
                  currentPlan.plan === 'free'
                    ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 text-white hover:shadow-xl'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-lg'
                }`}
              >
                <Package className="w-4 h-4 mr-2" />
                {t('quota_indicator.buy_pack')}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2 gap-1 xs:gap-0">
              <span>{t('quota_indicator.used', { count: quota.current })}</span>
              <span>
                {t('quota_indicator.total', {
                  count: quota.limit
                })}
              </span>
            </div>

            <div
              className={`w-full rounded-full h-2 sm:h-3 overflow-hidden ${
                currentPlan.plan === 'free'
                  ? 'bg-gradient-to-r from-blue-100 to-purple-100'
                  : 'bg-gray-200'
              }`}
            >
              <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${
                  currentPlan.plan === 'free'
                    ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500'
                    : ''
                }`}
                style={{
                  width: `${Math.min(quota.percentage, 100)}%`,
                  backgroundColor:
                    currentPlan.plan !== 'free' ? progressColor : undefined,
                }}
              />
            </div>
          </div>

          {/* Welcome offer -50% */}
          {/* {showWelcomeOffer && (
            <div className="mb-4">
              <div className="bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 rounded-xl p-4 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 via-red-500/20 to-pink-500/20 animate-pulse"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Gift className="w-6 h-6 mr-2 animate-bounce" />
                      <span className="font-bold text-lg">🎁 {t('quota_indicator.welcome_offer.title')}</span>
                    </div>
                    <div className="bg-white text-red-600 px-3 py-1 rounded-full font-bold text-sm animate-pulse">
                      -50%
                    </div>
                  </div>
                  <p className="text-sm mb-3 opacity-95">
                    🔓 <strong>{t('quota_indicator.welcome_offer.description')}</strong> — {t('quota_indicator.welcome_offer.exclusive')} !
                  </p>
                  <button
                    onClick={onUpgradeClick}
                    className="w-full bg-white text-red-600 py-2 px-4 rounded-lg font-bold hover:bg-gray-100 transition-all transform hover:scale-105 flex items-center justify-center"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('quota_indicator.welcome_offer.button')}
                  </button>
                </div>
              </div>
            </div>
          )} */}

          {/* Earn free analyses button */}
          {currentPlan.plan === 'free' && (
            <div className="mb-4">
              <button
                onClick={() => setShowEarnModal(true)}
                className="w-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 text-white py-3 px-4 rounded-lg font-semibold transition-all transform hover:scale-105 hover:shadow-lg flex items-center justify-center animate-pulse"
              >
                <Zap className="w-5 h-5 mr-2 animate-bounce" />
                🎁 {t('quota_indicator.earn_analyses')}
              </button>
            </div>
          )}

          {quota.percentage >= 80 && (
            <div className="flex items-start p-3 bg-orange-50 rounded-lg border border-orange-200">
              <AlertTriangle className="w-5 h-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-orange-800">
                  {quota.percentage >= 100
                    ? t('quota_indicator.quota_exhausted')
                    : t('quota_indicator.quota_almost_exhausted')}
                </p>
                <p className="text-orange-700 mt-1">
                  {quota.percentage >= 100
                    ? currentPlan.plan === 'free'
                      ? t('quota_indicator.quota_exhausted_free_message')
                      : t('quota_indicator.quota_exhausted_paid_message')
                    : t('quota_indicator.quota_warning', { count: remaining })}
                </p>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={onUpgradeClick}
                    className="text-orange-800 hover:text-orange-900 font-medium underline"
                  >
                    {t('quota_indicator.view_packs')} →
                  </button>
                  {currentPlan.plan === 'free' && (
                    <button
                      onClick={() => setShowEarnModal(true)}
                      className="text-orange-800 hover:text-orange-900 font-medium underline"
                    >
                      {t('quota_indicator.earn_analyses')} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentPlan.plan === 'free' &&
            quota.percentage < 50 &&
            !showWelcomeOffer && (
              <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-800">
                  <p className="font-medium">
                    {t('quota_indicator.discovery_pack_tip.title')}
                  </p>
                  <p className="mt-1">
                    {t('quota_indicator.discovery_pack_tip.description')}
                  </p>
                </div>
              </div>
            )}

          {/* {currentPlan.plan === 'free' && (
            <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-start">
                <Gift className="w-5 h-5 text-green-600 mr-2 mt-0.5 animate-bounce" />
                <div className="text-xs sm:text-sm text-green-800">
                  <p className="font-medium">
                    {t('quota_indicator.monthly_quota_info.title')}
                  </p>
                  <p className="mt-1">
                    {t('quota_indicator.monthly_quota_info.description')}
                  </p>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>

      <EarnAnalysesModal
        isOpen={showEarnModal}
        onClose={() => setShowEarnModal(false)}
        onSuccess={handleEarnSuccess}
      />
    </>
  );
};

export default QuotaIndicator;
